/** @jsx React.createElement */
/** @jsxFrag React.Fragment */
import {Button, Typography} from 'onboarding-components';
import * as React from 'react';

import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {Content, Header} from 'common/onboarding/immersive-onboarding/components';
import {AppContext, SetupGuideContext, Step} from 'common/onboarding/immersive-onboarding/types';
import {openDemoProjectSelectorModal} from 'common/widgets/demo-selector-modal';
import {IMMERSIVE_ASSETS} from 'common/onboarding/immersive-onboarding/assets/assets';
import {scrollToStep} from 'common/onboarding/immersive-onboarding/components/scroll-manager';
import {track, TrackingEvent, TrackingProperty} from 'common/onboarding/immersive-onboarding/tracking';
import {updateStepsCompleted} from 'common/report/queries/app-api/immersive-onboarding';
import {ordinalizeNumber} from 'common/util';
import {openMediaModal} from 'common/widgets/tutorial-video-modal';

import styles from './welcome.module.styl';

export const Welcome = () => {
  const {completed, setupGuideInfo, setSetupGuideInfo} = React.useContext(SetupGuideContext);

  React.useEffect(() => {
    if (setupGuideInfo && !setupGuideInfo?.steps_completed.includes(Step.Welcome)) {
      updateStepsCompleted(setupGuideInfo.project_id, [...setupGuideInfo.steps_completed, Step.Welcome]);
      setSetupGuideInfo({...setupGuideInfo, steps_completed: [...setupGuideInfo.steps_completed, Step.Welcome]});
    }
  }, [setupGuideInfo]);

  const WelcomeCTA = ({image, title, cta, name}) => {
    return (
      <>
        <div className={styles.welcomeCta} onClick={cta}>
          <img src={image} className={styles.welcomeCtaImage} />
          <Typography variant='headingXs' className={styles.welcomeCtaTitle}>
            {title}
          </Typography>
          <Button variant='inline' className={styles.welcomeCtaButton}>
            {name}
          </Button>
        </div>
      </>
    );
  };

  const {currentProject} = React.useContext(AppContext);
  const {userCount, name} = currentProject.get().value || {};

  const nextButtonCopy = userCount === 1 ? `Start Setup` : `Continue Setup`;

  function welcomeCTAs() {
    return (
      <>
        <Header
          title={`Get to know Mixpanel`}
          step={`welcome`}
          imgSrc={IMMERSIVE_ASSETS.friendlyX}
          completed={completed[Step.Welcome]}
        />
        <Content>
          <div className={styles.welcomeContent}>
            <div className={styles.welcomeTitle}>
              <Typography variant='headingXs' style={{margin: 0}}>
                Mixpanel will help you better understand your customers and answer product questions
              </Typography>
            </div>
            {userCount > 1 && (
              <div className={styles.welcomeSubTitle}>
                <Typography tagName='span' variant='bodySm'>
                  {`You're the `}
                </Typography>
                <Typography tagName='span' variant='bodySm' weight='bold'>
                  {ordinalizeNumber(userCount)} person to join {name} 🎉
                </Typography>
              </div>
            )}
            <div className={styles.welcomeSubTitle}>
              <Typography tagName='span' variant='bodySm'>
                When you're ready to begin, select{` `}
              </Typography>
              <Typography tagName='span' variant='bodySm' weight='bold'>
                {nextButtonCopy}
              </Typography>
            </div>
            <div className={styles.welcomeCtas}>
              <WelcomeCTA
                image={IMMERSIVE_ASSETS.welcomeVideo}
                title={`Learn how analytics helps businesses`}
                cta={() => {
                  openMediaModal(
                    (ev) => {
                      if (ev.type === `error`) {
                        addToast({message: ev.detail.message, type: ToastType.ERROR});
                      }
                    },
                    {short: true},
                  );
                  track(TrackingEvent.WelcomeResourceClick, {[TrackingProperty.Name]: `video`});
                }}
                name={`Watch Video - 1 min`}
              />
              <WelcomeCTA
                image={IMMERSIVE_ASSETS.welcomeDemo}
                title={`Explore a demo project from your industry`}
                cta={() => {
                  openDemoProjectSelectorModal(
                    (ev) => {
                      addToast({message: ev.detail.message, type: ToastType.ERROR});
                    },
                    {'is-new-tab': true},
                  );
                  track(TrackingEvent.WelcomeResourceClick, {[TrackingProperty.Name]: `demo project`});
                }}
                name={`Explore Demo`}
              />
              <WelcomeCTA
                image={IMMERSIVE_ASSETS.welcomeCompanies}
                title={`See how other companies use Mixpanel`}
                cta={() => {
                  window.open(`https://mixpanel.com/customers/all/?industry=&region=&segment=`, `_blank`);
                  track(TrackingEvent.WelcomeResourceClick, {[TrackingProperty.Name]: `stories`});
                }}
                name={`Read Stories`}
              />
            </div>
            <div>
              <Button
                onClick={() => {
                  scrollToStep(Step.Events);
                  track(TrackingEvent.WelcomeStartSetupClick);
                }}
              >
                {nextButtonCopy}
              </Button>
            </div>
          </div>
        </Content>
      </>
    );
  }

  return welcomeCTAs();
};
